import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useOrder } from '../../contexts/OrderContext';
import { useMembership } from '../../contexts/MembershipContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import PaymentQRCode from '../../components/payment/PaymentQRCode';
import PaymentStatusChecker from '../../components/payment/PaymentStatusChecker';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import formatOrderId from '../../utils/formatOrderId';

const { FiSmartphone, FiCheckCircle, FiClock, FiInfo, FiArrowRight, FiStar } = FiIcons;

const PaymentPage = () => {
  const { currentOrder, updateOrder, addOrder, setCurrentOrder, orders } = useOrder();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [processingTime, setProcessingTime] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('qr');
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  // Order IDs are generated centrally in OrderContext

  // Extract the membership usage function at component level
  // We need to do this to avoid the React Hook usage error inside handlePayment
  const membershipContext = useMembership();

  // FIX START: Use sessionStorage for persistence on refresh
  useEffect(() => {
    // If currentOrder is already set, no need to do anything
    if (currentOrder) {
      return;
    }

    // First, try to get the order from sessionStorage
    const storedOrder = sessionStorage.getItem('currentOrder');
    if (storedOrder) {
      try {
        const parsedOrder = JSON.parse(storedOrder);
        setCurrentOrder(parsedOrder);
        console.log("Found and restored order from sessionStorage:", parsedOrder);
      } catch (e) {
        console.error("Failed to parse order from sessionStorage", e);
        sessionStorage.removeItem('currentOrder');
      }
    } else {
      // Fallback: if no order in sessionStorage, try to get the last one from context orders
      const last = Array.isArray(orders) && orders.length > 0 ? orders[orders.length - 1] : null;
      if (last) {
        setCurrentOrder(last);
        console.log("Restored last order from context:", last);
      } else {
        console.log("No order found in sessionStorage or context.");
        // Optional: Redirect if no order is found at all
        // navigate('/services');
      }
    }
  }, [currentOrder, orders, setCurrentOrder, navigate]);
  // FIX END

  useEffect(() => {
    if (!currentOrder) {
      return;
    }

    // Calculate processing time based on current time
    const now = new Date();
    const hour = now.getHours();
    let startTime, endTime;

    // Default processing time if not specified
    const processingTimeStr = currentOrder.processingTime || '2-4 hours';
    const [min, max] = processingTimeStr.split('-').map(t => {
      const value = parseInt(t.trim().split(' ')[0]);
      return isNaN(value) ? 2 : value; // Default to 2 if parsing fails
    });

    if (hour >= 22 || hour < 7) {
      // After 10 PM or before 7 AM - will be processed next morning
      startTime = new Date(now);
      startTime.setHours(7, 0, 0, 0);
      if (hour >= 22) {
        startTime.setDate(startTime.getDate() + 1);
      }

      endTime = new Date(startTime);
      endTime.setHours(endTime.getHours() + max);
    } else {
      // During working hours
      startTime = new Date(now);
      endTime = new Date(now);
      endTime.setHours(endTime.getHours() + max);
      if (endTime.getHours() >= 22) {
        endTime.setHours(22, 0, 0, 0);
      }
    }

    const formatTime = (date) => {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDate = (date) => {
      return date.toLocaleDateString([], { day: '2-digit', month: 'short' });
    };

    const startTimeStr = `${formatTime(startTime)}, ${formatDate(startTime)}`;
    const endTimeStr = `${formatTime(endTime)}, ${formatDate(endTime)}`;
    setProcessingTime(`${startTimeStr} - ${endTimeStr}`);
  }, [currentOrder]);

  const handlePaymentSuccess = async () => {
    setPaymentConfirmed(true);
    await processPayment();
  };

  const handleManualPayment = async () => {
    setLoading(true);
    await processPayment();
  };

  const processPayment = async () => {
    try {
      setLoading(true);
      if (!currentOrder) {
        toast.error('No order found. Please create an order again.');
        navigate('/services');
        return;
      }
      
      // Use the centrally generated 15-char order ID from context
      const formattedOrderId = currentOrder.orderId;
      
      // Create the updated order object
      const updatedOrder = {
        ...currentOrder,
        id: formattedOrderId, // Ensure ID is set to the 15-digit order ID
        status: 'payment_confirmed',
        paymentMethod: 'upi',
        paymentDate: new Date().toISOString(),
        orderId: formattedOrderId,
        estimatedProcessingTime: processingTime,
        updatedAt: new Date().toISOString()
      };

      // If using membership, record the usage
      if (currentOrder.useMembership) {
        try {
          // Using membershipContext instead of directly calling the hook
          await membershipContext.useForApplication(currentOrder.id);
        } catch (error) {
          console.error("Error recording membership usage:", error);
          toast.error("Failed to apply membership benefits");
        }
      }

      // Update payment status in the database
      if (paymentConfirmed) {
        const { error } = await supabase
          .from('payment_qr_codes_f7k3lp')
          .update({ payment_status: 'completed' })
          .eq('order_id', formattedOrderId);
        
        if (error) {
          console.error('Error updating payment status:', error);
        }
      }

      // Update order status
      updateOrder(updatedOrder.id, updatedOrder);
      addOrder(updatedOrder);
      setCurrentOrder(updatedOrder);

      // FIX: Store the full order object, not just the ID
      sessionStorage.setItem('currentOrder', JSON.stringify(updatedOrder));
      
      // Store the order ID in session storage for receipt page
      sessionStorage.setItem('lastCompletedOrderId', updatedOrder.id);
      
      toast.success('Payment successful!');
      
      // Navigate to the receipt page
      navigate(`/payment-receipt/${updatedOrder.id}`);
    } catch (error) {
      console.error("Payment error:", error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!currentOrder) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Order Found</h2>
          <p className="text-gray-600 mb-6">Please select a service and proceed again.</p>
          <Button onClick={() => navigate('/services')}>Go to Services</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900">Payment</h1>
          <p className="text-gray-600 mt-2">Complete your payment to proceed with your application</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Payment Methods */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              {/* Membership Applied Banner */}
              {currentOrder.useMembership && (
                <div className="mb-6 bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <SafeIcon icon={FiStar} className="w-6 h-6 text-green-600 mr-3" />
                    <div>
                      <h3 className="font-medium text-green-800">Membership Applied!</h3>
                      <p className="text-sm text-green-700">
                        Your membership has been applied to this order. You're only paying the application fee.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Pay via UPI</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPaymentMethod('qr')}
                    className={`px-3 py-1 text-sm rounded-full ${
                      paymentMethod === 'qr'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    QR Code
                  </button>
                  <button
                    onClick={() => setPaymentMethod('manual')}
                    className={`px-3 py-1 text-sm rounded-full ${
                      paymentMethod === 'manual'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Manual Entry
                  </button>
                </div>
              </div>

              {paymentMethod === 'qr' ? (
                <>
                  <PaymentQRCode 
                    order={{
                      ...currentOrder,
                      orderId: currentOrder.orderId
                    }} 
                    onPaymentSuccess={handlePaymentSuccess} 
                  />
                  
                  {!paymentConfirmed && (
                    <PaymentStatusChecker 
                      orderId={currentOrder.orderId}
                      onPaymentConfirmed={handlePaymentSuccess}
                    />
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center mb-8">
                  <div className="bg-gray-100 p-4 rounded-lg mb-4 w-full text-center">
                    <h3 className="font-medium text-gray-900 mb-2">UPI Payment Details</h3>
                    <p className="text-gray-700 font-medium">UPI ID: how2apply@ybl</p>
                    <p className="text-sm text-gray-600 mt-2">Amount: ₹{currentOrder.totalAmount}</p>
                    <p className="text-xs text-gray-500 mt-1 font-mono">Reference: {formatOrderId(currentOrder.orderId)}</p>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Pay using any UPI app and click "Payment Completed" below</p>

                  <div className="bg-blue-50 p-4 rounded-lg mb-6 mt-6 w-full">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <SafeIcon icon={FiInfo} className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          After making the payment through your UPI app, click the "Payment Completed" button below.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <a
                      href={`upi://pay?pa=how2apply@ybl&pn=How2Apply&am=${encodeURIComponent(currentOrder.totalAmount || 0)}&cu=INR&tn=${encodeURIComponent('Payment for Order ' + (currentOrder.orderId || ''))}`}
                      className="w-full inline-flex items-center justify-center bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 transition-colors"
                    >
                      Pay with PhonePe
                    </a>
                    <a
                      href={`upi://pay?pa=how2apply@ybl&pn=How2Apply&am=${encodeURIComponent(currentOrder.totalAmount || 0)}&cu=INR&tn=${encodeURIComponent('Payment for Order ' + (currentOrder.orderId || ''))}`}
                      className="w-full inline-flex items-center justify-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Pay with Google Pay
                    </a>
                  </div>

                  <div className="mt-6 w-full">
                    <Button 
                      onClick={handleManualPayment} 
                      loading={loading} 
                      className="w-full flex items-center justify-center" 
                      size="lg"
                    >
                      Payment Completed
                      <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                  
                  {/* Test button for development */}
                  <div className="mt-4 w-full">
                    <Button 
                      onClick={handleManualPayment} 
                      variant="outline"
                      className="w-full" 
                      size="sm"
                    >
                      Test: Skip Payment (Development)
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card className="p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
              <div className="space-y-3 text-sm mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service</span>
                  <span className="font-medium text-gray-900">{currentOrder.serviceName || currentOrder.serviceType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Category</span>
                  <span className="font-medium text-gray-900">{currentOrder.category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service Type</span>
                  <span className="font-medium text-gray-900 capitalize">{currentOrder.serviceType} Service</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Exam Fee</span>
                  <span className="font-medium text-gray-900">₹{currentOrder.examFee || 0}</span>
                </div>
                {currentOrder.useMembership ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Fee</span>
                    <span className="flex items-center">
                      <span className="line-through text-gray-400 mr-2">₹{currentOrder.serviceFee || 0}</span>
                      <span className="text-green-600 font-medium">₹0</span>
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service Fee</span>
                    <span className="font-medium text-gray-900">₹{currentOrder.serviceFee || 0}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Bank Charges and Taxes</span>
                  <span className="font-medium text-gray-900">₹{currentOrder.bankCharge || 0}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold">
                  <span className="text-gray-900">Total</span>
                  <span className="text-gray-900">₹{currentOrder.totalAmount || 0}</span>
                </div>
              </div>
              <div className="bg-primary-50 p-4 rounded-lg space-y-3">
                <div className="flex items-start space-x-2">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-primary-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Estimated Processing</h4>
                    <p className="text-sm text-gray-600">{processingTime}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-primary-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900">Processing Hours</h4>
                    <p className="text-sm text-gray-600">
                      7:00 AM - 10:00 PM<br />
                      Orders after 10 PM processed next morning
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 bg-green-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">Secure Payment</h4>
                    <p className="text-xs text-gray-600">
                      All transactions are secure and encrypted
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;